/**
 * A horizontally scrolled row that runs past the content grid to the viewport edges,
 * with the previous/next buttons the design draws under it (Figma `6377:87682`).
 *
 * **This is where the "carousel or scroll?" question got answered.** The design shows
 * arrows, so there are arrows - but they scroll a real overflow container rather than
 * driving a slider. That means it works with a trackpad, a touch screen, a keyboard and
 * a screen reader before any JavaScript runs, and the buttons are an addition rather
 * than the only way through. A slider that hides content behind JavaScript would look
 * the same and behave worse.
 *
 * **The row stays inside the content grid** (changed 2026-10-30: it used to bleed to the
 * viewport edges with the `left-1/2` trick). Cards that run past the grid's edge are
 * clipped by the track, and the buttons bring them into view.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../components/ui/Icon';

/**
 * The scrolling half, without any layout opinion, so a section can put the controls
 * where its design puts them - under the row for `pillars`, above it for `timeline`.
 */
/**
 * Classes every horizontal track shares.
 *
 * **The scrolling is instant, on purpose.** Both ways of animating it were measured in
 * a real browser and both silently did nothing: `scrollTo({behavior: 'smooth'})` never
 * moved the element, and CSS `scroll-behavior: smooth` stopped even a direct
 * `scrollLeft` assignment from taking effect. A button that animates on some engines and
 * does nothing on others is worse than one that always works, so the animation is out
 * until it can be shown to work. Putting it back is one class.
 *
 * The scrollbar is hidden because the design has none and the buttons stand in for it.
 * The track is focusable, so it stays reachable and scrollable with a keyboard.
 */
export const TRACK =
  'flex snap-x gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

export function useScroller() {
  const track = useRef<HTMLDivElement>(null);
  // Whether there is anything left to scroll in each direction. Without this the
  // buttons are always enabled, so on a row whose cards already fit they look broken -
  // you press them and nothing happens, because there is nowhere to go.
  const [range, setRange] = useState({ back: false, forward: false });

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;

    // **The start is not zero.** The cards carry `snap-start` and the track is padded to
    // the content grid, so the browser rests the row against the first card's edge -
    // which is `padding-left`, not 0. Comparing against 0 left the "previous" button
    // enabled at the start for ever: pressing it did nothing, because the row was
    // already as far back as it goes.
    const start = parseFloat(getComputedStyle(el).paddingLeft) || 0;

    // A pixel of slack at each end: sub-pixel layout means scrollLeft rarely lands on
    // the exact value.
    setRange({ back: el.scrollLeft > start + 1, forward: el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // The row's width changes with the viewport, and its children arrive with the CMS
    // payload, so one measurement at mount is not enough.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => { el.removeEventListener('scroll', measure); observer.disconnect(); };
  }, [measure]);

  const scrollBy = (direction: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    // One card plus its gap, taken from the first child so the step follows the layout.
    const first = el.firstElementChild as HTMLElement | null;
    const step = first ? first.offsetWidth + 16 : el.clientWidth * 0.8;
    el.scrollLeft += step * direction;
    measure();
  };

  /** Bring one child into view, for a section with its own index (a year, a tab). */
  const scrollTo = (index: number) => {
    const el = track.current;
    const child = el?.children[index] as HTMLElement | undefined;
    if (!el || !child) return;
    // **Not `scrollIntoView`**: it scrolls every ancestor that can scroll, and a parent
    // with `overflow: hidden` (the timeline's band) can be scrolled by script - so the
    // whole band, and the page's content with it, slid sideways under the clip.
    const start = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    el.scrollLeft += child.getBoundingClientRect().left - el.getBoundingClientRect().left - start;
    measure();
  };

  return { track, scrollBy, scrollTo, range };
}

/**
 * Round previous/next buttons, as drawn under the pillars row.
 *
 * A button is disabled when there is nothing left that way. The design draws them
 * always-on, but an always-on control that does nothing is the thing people report as
 * broken, and it is the one state the mock-up could not show.
 */
export function ScrollerButtons({
  label, scrollBy, range, tone = 'light'
}: {
  label: string;
  scrollBy: (d: 1 | -1) => void;
  range?: { back: boolean; forward: boolean };
  tone?: 'light' | 'dark';
}) {
  const style = tone === 'dark'
    ? 'border-white/30 text-white/70 hover:border-white hover:text-white'
    : 'border-ink/20 text-ink-muted hover:border-ink/50 hover:text-ink';

  return (
    <div className="flex gap-3">
      {([-1, 1] as const).map((direction) => {
        const enabled = !range || (direction === -1 ? range.back : range.forward);
        return (
          <button
            key={direction}
            type="button"
            onClick={() => scrollBy(direction)}
            disabled={!enabled}
            aria-label={`${label}: ${direction === -1 ? 'poprzednie' : 'następne'}`}
            className={`grid h-9 w-9 place-items-center rounded-full border transition ${style} disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-inherit`}
          >
            <Icon name="arrow-right" size={16} className={direction === -1 ? 'rotate-180' : ''} />
          </button>
        );
      })}
    </div>
  );
}

export function Scroller({ children, label }: { children: ReactNode; label: string }) {
  const { track, scrollBy, range } = useScroller();

  return (
    <div>
      <div
        ref={track}
        role="group"
        aria-label={label}
        tabIndex={0}
        className={`${TRACK} pb-2`}
      >
        {children}
      </div>

      <div className="mt-6">
        <ScrollerButtons label={label} scrollBy={scrollBy} range={range} />
      </div>
    </div>
  );
}
